import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Play, RefreshCw, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function DiscoverySettings() {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [quotas, setQuotas] = useState<any[]>([]);
  const [recentJobs, setRecentJobs] = useState<any[]>([]);
  const [searchQueries, setSearchQueries] = useState(`("new AI chatbot" OR "GPT chatbot alternative" OR "AI writing assistant" OR "AI code assistant" OR "educational AI tutor" OR "creative writing AI chatbot" OR "business intelligence assistant" OR "healthcare AI chatbot" OR "medical chatbot" OR "finance AI assistant" OR "banking chatbot" OR "e-commerce chatbot" OR "shopping chatbot" OR "travel AI assistant" OR "booking chatbot" OR "HR recruitment chatbot" OR "legal AI assistant" OR "lawyer chatbot" OR "real estate chatbot") (2024 OR 2025) platform`);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch API quotas
      const { data: quotaData, error: quotaError } = await supabase
        .from('api_quotas')
        .select('*')
        .in('service_name', ['serpapi', 'firecrawl']);

      if (quotaError) throw quotaError;
      setQuotas(quotaData || []);

      // Fetch recent discovery jobs
      const { data: jobsData, error: jobsError } = await supabase
        .from('discovery_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (jobsError) throw jobsError;
      setRecentJobs(jobsData || []);
    } catch (error: any) {
      toast({
        title: 'Error loading data',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const triggerDiscovery = async () => {
    setTriggering(true);
    try {
      const { data, error } = await supabase.functions.invoke('discovery-scheduler', {
        body: { source: 'manual_trigger' }
      });

      if (error) throw error;

      toast({
        title: 'Discovery triggered',
        description: data.action === 'cbd_paused' 
          ? 'Discovery is paused due to insufficient quota' 
          : 'Discovery job started successfully',
      });

      // Refresh data
      await fetchData();
    } catch (error: any) {
      toast({
        title: 'Error triggering discovery',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTriggering(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access Denied: Admin privileges required
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Bot Discovery Settings</h1>
        <p className="text-muted-foreground">
          Manage automated bot discovery, search queries, and API quotas
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="queries">Search Queries</TabsTrigger>
          <TabsTrigger value="history">Discovery History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* API Quotas */}
          <Card>
            <CardHeader>
              <CardTitle>API Quota Status</CardTitle>
              <CardDescription>
                Current usage of discovery APIs (resets monthly)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading quota data...</span>
                </div>
              ) : (
                quotas.map((quota) => (
                  <div key={quota.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium capitalize">{quota.service_name}</span>
                      <Badge variant={quota.used_this_month > quota.monthly_limit * 0.8 ? 'destructive' : 'default'}>
                        {quota.used_this_month} / {quota.monthly_limit}
                      </Badge>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ 
                          width: `${(quota.used_this_month / quota.monthly_limit) * 100}%` 
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Resets on: {new Date(quota.reset_date).toLocaleDateString()}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Manual Trigger */}
          <Card>
            <CardHeader>
              <CardTitle>Manual Discovery Trigger</CardTitle>
              <CardDescription>
                Run the discovery process immediately (normally runs daily at 2 AM)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={triggerDiscovery} 
                disabled={triggering}
                className="w-full"
              >
                {triggering ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running Discovery...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Trigger Discovery Now
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Cron Schedule Info */}
          <Card>
            <CardHeader>
              <CardTitle>Automated Schedule</CardTitle>
              <CardDescription>
                Discovery runs automatically every day
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Schedule:</span>
                  <Badge>Daily at 2:00 AM</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge variant="default">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="queries" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Search Queries</CardTitle>
              <CardDescription>
                Customize the search queries used to discover new chatbots. Each query should be on a new line.
                The system rotates through these queries daily.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={searchQueries}
                onChange={(e) => setSearchQueries(e.target.value)}
                rows={15}
                className="font-mono text-sm"
                placeholder="Enter search queries, one per line..."
              />
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Note: Search query customization requires updating the edge function. 
                  Copy these queries and ask the AI to update the discovery-scheduler function.
                </AlertDescription>
              </Alert>
              <div className="text-sm text-muted-foreground">
                Current count: {searchQueries.split('\n').filter(q => q.trim()).length} queries
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Discovery History</CardTitle>
                  <CardDescription>
                    Recent discovery job executions
                  </CardDescription>
                </div>
                <Button onClick={fetchData} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading history...</span>
                </div>
              ) : recentJobs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No discovery jobs found
                </p>
              ) : (
                <div className="space-y-4">
                  {recentJobs.map((job) => (
                    <div 
                      key={job.id} 
                      className="border rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{job.search_query}</span>
                        <Badge variant={
                          job.status === 'completed' ? 'default' :
                          job.status === 'running' ? 'secondary' :
                          'destructive'
                        }>
                          {job.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Found:</span>
                          <span className="ml-2 font-medium">{job.total_found}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Processed:</span>
                          <span className="ml-2 font-medium">{job.processed_count}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Success:</span>
                          <span className="ml-2 font-medium text-green-600">{job.success_count}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Errors:</span>
                          <span className="ml-2 font-medium text-red-600">{job.error_count}</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(job.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}